import React, { useMemo, useState } from "react";
import {
    createInvitation,
    fetchCrmAccountPrimaryContact,
    searchCrmAccountsByCompany,
    searchCrmContactsByEmail,
} from "../services/api";
import "../styles/invitations.css";
import { buildPublicAppUrl } from "../services/publicAppUrl";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_EXPIRY_DAYS = "7";

const initialFormState = {
    clientEmail: "",
    clientFirstName: "",
    clientLastName: "",
    company: "",
    provider: "",
    expiresInDays: DEFAULT_EXPIRY_DAYS,
};

export default function CreateInvitation() {
    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [generatedLink, setGeneratedLink] = useState("");
    const [copied, setCopied] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [crmOpen, setCrmOpen] = useState(false);
    const [crmSearchType, setCrmSearchType] = useState("email");
    const [crmSearchValue, setCrmSearchValue] = useState("");
    const [crmResults, setCrmResults] = useState([]);
    const [crmLoading, setCrmLoading] = useState(false);
    const [crmError, setCrmError] = useState("");
    const [crmNoResults, setCrmNoResults] = useState(false);
    const [crmSelectedKey, setCrmSelectedKey] = useState("");
    const [crmRecordId, setCrmRecordId] = useState("");
    const [crmModule, setCrmModule] = useState("");
    const [crmApplyLoading, setCrmApplyLoading] = useState(false);
    const [crmAutofill, setCrmAutofill] = useState({
        clientFirstName: false,
        clientLastName: false,
        clientEmail: false,
        company: false,
    });

    const linkPreview = useMemo(() => {
        if (!generatedLink) return "";
        return generatedLink;
    }, [generatedLink]);

    const selectedCrmRecord = useMemo(() => {
        if (!crmSelectedKey) return null;
        return (
            crmResults.find(
                (record) => `${record.module}:${record.id}` === crmSelectedKey
            ) || null
        );
    }, [crmResults, crmSelectedKey]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
        setErrorMessage("");
        setCrmAutofill((prev) => {
            if (!prev[name]) {
                return prev;
            }
            return { ...prev, [name]: false };
        });
    };

    const validate = () => {
        const nextErrors = {};
        if (!formData.clientFirstName.trim()) {
            nextErrors.clientFirstName = "First name is required.";
        }
        if (!formData.clientLastName.trim()) {
            nextErrors.clientLastName = "Last name is required.";
        }
        if (!formData.clientEmail.trim()) {
            nextErrors.clientEmail = "Email is required.";
        } else if (!EMAIL_REGEX.test(formData.clientEmail.trim())) {
            nextErrors.clientEmail = "Enter a valid email address.";
        }
        if (!formData.company.trim()) {
            nextErrors.company = "Company name is required.";
        }
        if (!formData.provider.trim()) {
            nextErrors.provider = "Provider name is required.";
        }
        if (!formData.expiresInDays) {
            nextErrors.expiresInDays = "Select an expiration window.";
        }
        return nextErrors;
    };

    const handleCrmSearch = async () => {
        const query = crmSearchValue.trim();
        setCrmError("");
        setCrmNoResults(false);
        setCrmResults([]);
        setCrmSelectedKey("");

        if (!query) {
            setCrmError(
                crmSearchType === "email"
                    ? "Enter an email address to search."
                    : "Enter a company name to search."
            );
            return;
        }

        if (crmSearchType === "email" && !EMAIL_REGEX.test(query)) {
            setCrmError("Enter a valid email address.");
            return;
        }

        if (crmSearchType === "company" && query.length < 2) {
            setCrmError("Enter at least 2 characters.");
            return;
        }

        setCrmLoading(true);
        try {
            const results =
                crmSearchType === "email"
                    ? await searchCrmContactsByEmail(query)
                    : await searchCrmAccountsByCompany(query);

            setCrmResults(results);
            if (!results || results.length === 0) {
                setCrmNoResults(true);
            }
        } catch (error) {
            setCrmError(
                error?.message || "CRM search failed. Please try again."
            );
        } finally {
            setCrmLoading(false);
        }
    };

    const handleUseSelectedCrm = async () => {
        if (!selectedCrmRecord) {
            setCrmError("Select a CRM record to use.");
            return;
        }

        setCrmApplyLoading(true);
        setCrmError("");

        let mergedRecord = { ...selectedCrmRecord };
        try {
            if (selectedCrmRecord.module === "Accounts") {
                const primaryContact = await fetchCrmAccountPrimaryContact(
                    selectedCrmRecord.id
                );
                if (primaryContact) {
                    mergedRecord = {
                        ...selectedCrmRecord,
                        firstName:
                            selectedCrmRecord.firstName ||
                            primaryContact.firstName,
                        lastName:
                            selectedCrmRecord.lastName ||
                            primaryContact.lastName,
                        email:
                            selectedCrmRecord.email || primaryContact.email,
                    };
                }
            }
        } catch (error) {
            setCrmError(
                error?.message ||
                    "Unable to load account contact details right now."
            );
        } finally {
            setCrmApplyLoading(false);
        }

        setFormData((prev) => {
            const next = { ...prev };

            if (mergedRecord.firstName) {
                next.clientFirstName = mergedRecord.firstName;
            }
            if (mergedRecord.lastName) {
                next.clientLastName = mergedRecord.lastName;
            }
            if (mergedRecord.email) {
                next.clientEmail = mergedRecord.email;
            }
            if (mergedRecord.company) {
                next.company = mergedRecord.company;
            }

            return next;
        });

        setErrors((prev) => ({
            ...prev,
            clientFirstName: "",
            clientLastName: "",
            clientEmail: "",
            company: "",
        }));

        setCrmAutofill({
            clientFirstName: Boolean(mergedRecord.firstName),
            clientLastName: Boolean(mergedRecord.lastName),
            clientEmail: Boolean(mergedRecord.email),
            company: Boolean(mergedRecord.company),
        });

        setCrmRecordId(mergedRecord.id || "");
        setCrmModule(mergedRecord.module || "");
        setCrmError("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (generatedLink) return;
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        try {
            const invitation = await createInvitation({
                clientEmail: formData.clientEmail,
                clientFirstName: formData.clientFirstName,
                clientLastName: formData.clientLastName,
                company: formData.company,
                provider: formData.provider,
                expiresInDays: formData.expiresInDays,
                crmRecordId: crmRecordId || undefined,
                crmModule: crmModule || undefined,
            });

            const link = buildPublicAppUrl(`/submit/${invitation.id}`);
            setGeneratedLink(link);
            setCopied(false);
            if (invitation.deliveryStatus === "accepted") {
                setSuccessMessage("Invitation created. The email provider accepted the message for delivery.");
            } else {
                setSuccessMessage("");
                setErrorMessage(
                    "Invitation created, but the email was not confirmed as sent. Copy the link below and share it with the client securely. Do not create another invitation."
                );
            }
        } catch (error) {
            setErrorMessage(
                error?.message || "Unable to create the invitation right now."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async () => {
        if (!generatedLink) {
            return;
        }
        try {
            await navigator.clipboard.writeText(generatedLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            setCopied(false);
        }
    };

    const handleReset = () => {
        setFormData(initialFormState);
        setErrors({});
        setGeneratedLink("");
        setCopied(false);
        setSuccessMessage("");
        setErrorMessage("");
        setCrmOpen(false);
        setCrmSearchType("email");
        setCrmSearchValue("");
        setCrmResults([]);
        setCrmLoading(false);
        setCrmError("");
        setCrmNoResults(false);
        setCrmSelectedKey("");
        setCrmRecordId("");
        setCrmModule("");
        setCrmApplyLoading(false);
        setCrmAutofill({
            clientFirstName: false,
            clientLastName: false,
            clientEmail: false,
            company: false,
        });
    };

    return (
        <main className="invitations-page">
            <div className="content-shell">
                <div className="invitation-card">
                    <div className="invitation-card-header">
                        <h1>Create invitation</h1>
                        <p>
                            Generate a secure link so clients can submit their
                            credentials.
                        </p>
                    </div>
                    <div className="crm-section">
                        <button
                            className="crm-toggle"
                            type="button"
                            onClick={() => setCrmOpen((prev) => !prev)}
                            aria-expanded={crmOpen}
                            aria-controls="crm-panel"
                        >
                            <span>Search CRM</span>
                            <span className="crm-toggle-state">
                                {crmOpen ? "Hide" : "Show"}
                            </span>
                        </button>
                        {crmOpen && (
                            <div className="crm-panel" id="crm-panel">
                                <div className="crm-controls">
                                    <div className="crm-field">
                                        <label htmlFor="crmSearchInput">
                                            Search term
                                        </label>
                                        <input
                                            id="crmSearchInput"
                                            type="text"
                                            value={crmSearchValue}
                                            onChange={(event) =>
                                                setCrmSearchValue(event.target.value)
                                            }
                                            placeholder={
                                                crmSearchType === "email"
                                                    ? "client@email.com"
                                                    : "Company name"
                                            }
                                        />
                                    </div>
                                    <div className="crm-field">
                                        <span className="crm-label">
                                            Search type
                                        </span>
                                        <div className="crm-radio-group">
                                            <label className="crm-radio">
                                                <input
                                                    type="radio"
                                                    name="crmSearchType"
                                                    value="email"
                                                    checked={crmSearchType === "email"}
                                                    onChange={() =>
                                                        setCrmSearchType("email")
                                                    }
                                                />
                                                <span>By email</span>
                                            </label>
                                            <label className="crm-radio">
                                                <input
                                                    type="radio"
                                                    name="crmSearchType"
                                                    value="company"
                                                    checked={
                                                        crmSearchType === "company"
                                                    }
                                                    onChange={() =>
                                                        setCrmSearchType("company")
                                                    }
                                                />
                                                <span>By company</span>
                                            </label>
                                        </div>
                                    </div>
                                    <button
                                        className="btn secondary-btn"
                                        type="button"
                                        onClick={handleCrmSearch}
                                        disabled={crmLoading}
                                    >
                                        {crmLoading ? "Searching..." : "Search"}
                                    </button>
                                </div>
                                {crmError && (
                                    <div className="alert error-alert">
                                        {crmError}
                                    </div>
                                )}
                                {crmNoResults && !crmLoading && (
                                    <div className="crm-status">
                                        No results found. You can continue with
                                        manual entry.
                                    </div>
                                )}
                                {crmResults.length > 0 && (
                                    <div className="crm-results">
                                        <div className="crm-results-header">
                                            <span>Search results</span>
                                            <button
                                                className="btn ghost-btn"
                                                type="button"
                                                onClick={handleUseSelectedCrm}
                                                disabled={
                                                    !selectedCrmRecord ||
                                                    crmApplyLoading
                                                }
                                            >
                                                {crmApplyLoading
                                                    ? "Applying..."
                                                    : "Use selected"}
                                            </button>
                                        </div>
                                        <ul className="crm-results-list">
                                            {crmResults.map((record) => {
                                                const name = [
                                                    record.firstName,
                                                    record.lastName,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" ");
                                                const primary =
                                                    name || record.company || "Unnamed";
                                                const secondary = [
                                                    record.email,
                                                    record.company &&
                                                    name &&
                                                    record.company !== name
                                                        ? record.company
                                                        : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" \u2022 ");
                                                const recordKey = `${record.module}:${record.id}`;

                                                return (
                                                    <li
                                                        key={recordKey}
                                                        className="crm-result"
                                                    >
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                name="crmSelected"
                                                                value={recordKey}
                                                                checked={
                                                                    crmSelectedKey ===
                                                                    recordKey
                                                                }
                                                                onChange={(event) =>
                                                                    setCrmSelectedKey(
                                                                        event.target.value
                                                                    )
                                                                }
                                                            />
                                                            <span className="crm-result-text">
                                                                <span className="crm-result-primary">
                                                                    {primary}
                                                                </span>
                                                                {secondary && (
                                                                    <span className="crm-result-secondary">
                                                                        {secondary}
                                                                    </span>
                                                                )}
                                                                <span className="crm-result-module">
                                                                    {record.module}
                                                                </span>
                                                            </span>
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                                {crmRecordId && crmModule && (
                                    <div className="crm-status">
                                        Linked to CRM record ({crmModule}):{" "}
                                        {crmRecordId}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <form className="invitation-form" onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-field">
                                <label htmlFor="clientFirstName">
                                    Client first name
                                    {crmAutofill.clientFirstName && (
                                        <span className="crm-badge">CRM</span>
                                    )}
                                </label>
                                <input
                                    id="clientFirstName"
                                    name="clientFirstName"
                                    type="text"
                                    value={formData.clientFirstName}
                                    onChange={handleChange}
                                    placeholder="First name"
                                />
                                {errors.clientFirstName && (
                                    <span className="field-error-text">
                                        {errors.clientFirstName}
                                    </span>
                                )}
                            </div>
                            <div className="form-field">
                                <label htmlFor="clientLastName">
                                    Client last name
                                    {crmAutofill.clientLastName && (
                                        <span className="crm-badge">CRM</span>
                                    )}
                                </label>
                                <input
                                    id="clientLastName"
                                    name="clientLastName"
                                    type="text"
                                    value={formData.clientLastName}
                                    onChange={handleChange}
                                    placeholder="Last name"
                                />
                                {errors.clientLastName && (
                                    <span className="field-error-text">
                                        {errors.clientLastName}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="form-field">
                            <label htmlFor="clientEmail">
                                Client email
                                {crmAutofill.clientEmail && (
                                    <span className="crm-badge">CRM</span>
                                )}
                            </label>
                            <input
                                id="clientEmail"
                                name="clientEmail"
                                type="email"
                                value={formData.clientEmail}
                                onChange={handleChange}
                                placeholder="client@email.com"
                            />
                            {errors.clientEmail && (
                                <span className="field-error-text">
                                    {errors.clientEmail}
                                </span>
                            )}
                        </div>
                        <div className="form-field">
                            <label htmlFor="company">
                                Company name
                                {crmAutofill.company && (
                                    <span className="crm-badge">CRM</span>
                                )}
                            </label>
                            <input
                                id="company"
                                name="company"
                                type="text"
                                value={formData.company}
                                onChange={handleChange}
                                placeholder="Company name"
                            />
                            {errors.company && (
                                <span className="field-error-text">
                                    {errors.company}
                                </span>
                            )}
                        </div>
                        <div className="form-field">
                            <label htmlFor="provider">
                                Accreditation provider
                            </label>
                            <input
                                id="provider"
                                name="provider"
                                type="text"
                                value={formData.provider}
                                onChange={handleChange}
                                placeholder="Provider name"
                            />
                            {errors.provider && (
                                <span className="field-error-text">
                                    {errors.provider}
                                </span>
                            )}
                        </div>
                        <div className="form-field">
                            <label htmlFor="expiresInDays">
                                Expiration window
                            </label>
                            <select
                                id="expiresInDays"
                                name="expiresInDays"
                                value={formData.expiresInDays}
                                onChange={handleChange}
                            >
                                <option value="7">7 days</option>
                                <option value="14">14 days</option>
                                <option value="30">30 days</option>
                            </select>
                            {errors.expiresInDays && (
                                <span className="field-error-text">
                                    {errors.expiresInDays}
                                </span>
                            )}
                        </div>
                        {errorMessage && (
                            <div className="alert error-alert">
                                {errorMessage}
                            </div>
                        )}
                        {successMessage && (
                            <div className="alert success-alert">
                                {successMessage}
                            </div>
                        )}
                        <button className="btn" type="submit" disabled={loading || Boolean(generatedLink)}>
                            {loading ? "Creating..." : "Create invitation"}
                        </button>
                    </form>
                </div>

                {generatedLink && (
                    <div className="invitation-card">
                        <div className="invitation-card-header">
                            <h2>Invitation link</h2>
                            <p>Copy and share this link with your client.</p>
                        </div>
                        <div className="generated-link-display">
                            <input
                                type="text"
                                value={linkPreview}
                                readOnly
                            />
                            <button
                                className="btn secondary-btn"
                                type="button"
                                onClick={handleCopy}
                            >
                                {copied ? "Copied!" : "Copy link"}
                            </button>
                        </div>
                        <div className="qr-placeholder">
                            QR code coming soon
                        </div>
                        <button
                            className="btn ghost-btn"
                            type="button"
                            onClick={handleReset}
                        >
                            Send another invitation
                        </button>
                    </div>
                )}
            </div>
        </main>
    );
}
